// TypeFormService.js
//
// System-type form templates live under `_nodetypes/{typeKey}/forms/{formKey}`.
// They are live for every base that uses the type (no per-base provision).
// Layout + blocksByKey live on the form document (same shape as `_forms`).

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';
import {
  buildDefaultFormLayoutScaffold,
  DEFAULT_FORM_DESCRIPTION,
  DEFAULT_FORM_KEY,
  DEFAULT_FORM_TITLE,
  getVisibleFormFieldKeys,
} from 'services/forms/FormModel';
import { ensureLayoutHasAssignedBlock } from 'services/layouts/LayoutModel';
import { sortLayoutsByConfiguredOrder } from 'utilities/LayoutOrder';

const NODETYPES_COLLECTION = collections.nodetypes;
const TYPE_FORMS_SUBCOLLECTION = collections.nodeTypeForms || 'forms';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {object|null|undefined} form
 * @returns {boolean}
 */
export function isSystemTypeForm(form) {
  if (!form || typeof form !== 'object') return false;
  if (form.isSystemForm === true) return true;
  if (normalizeString(form.formSource).toUpperCase() === 'SYSTEM_TYPE') return true;
  if (normalizeString(form.scope).toUpperCase() === 'SYSTEM_TYPE') return true;
  return false;
}

/**
 * @param {string} typeKey
 * @param {object} data
 * @param {string} [docId]
 * @returns {object}
 */
export function normalizeSystemTypeFormRow(typeKey, data = {}, docId = '') {
  const cleanedTypeKey = normalizeString(typeKey);
  const key = normalizeString(data?.key) || normalizeString(docId);
  return {
    ...(data || {}),
    id: key || normalizeString(docId),
    key: key || normalizeString(docId),
    typeKey: cleanedTypeKey || normalizeString(data?.typeKey),
    scope: 'SYSTEM_TYPE',
    isSystemForm: true,
    formSource: 'SYSTEM_TYPE',
    baseKey: null,
    canDelete: data?.isDefault === true ? false : Boolean(data?.canDelete !== false && data?.isDefault !== true),
  };
}

function typeFormsCollectionRef(typeKey) {
  return collection(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_FORMS_SUBCOLLECTION,
  );
}

function typeFormDocRef(typeKey, formKey) {
  return doc(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_FORMS_SUBCOLLECTION,
    normalizeString(formKey),
  );
}

/**
 * Subscribe to system forms for a root node type.
 *
 * @param {string} typeKey
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToTypeForms(typeKey, callback) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey || typeof callback !== 'function') {
    if (typeof callback === 'function') callback([]);
    return () => {};
  }

  return onSnapshot(
    typeFormsCollectionRef(cleanedTypeKey),
    (snapshot) => {
      const rows = sortLayoutsByConfiguredOrder(
        snapshot.docs.map((docSnap) => (
          normalizeSystemTypeFormRow(cleanedTypeKey, docSnap.data() || {}, docSnap.id)
        )),
      );
      callback(rows);
    },
    (error) => {
      console.error('[TypeFormService] subscribeToTypeForms error:', error);
      callback([]);
    },
  );
}

/**
 * Create or replace a system-type form template.
 *
 * @param {string} typeKey
 * @param {object} form
 * @returns {Promise<object>}
 */
export async function saveTypeForm(typeKey, form = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) {
    throw new Error('typeKey is required to save a system form.');
  }

  const formKey = normalizeString(form?.key) || generateKey();
  const now = Timestamp.now();
  const existingCreated = form?.dateCreated || null;
  const title = normalizeString(form?.title) || DEFAULT_FORM_TITLE;
  const isDefault = Boolean(form?.isDefault) || formKey === DEFAULT_FORM_KEY;
  const { blocksByKey, layout } = ensureLayoutHasAssignedBlock({
    blocksByKey: form?.blocksByKey,
    layout: form?.layout,
  });

  const payload = {
    ...form,
    key: formKey,
    typeKey: cleanedTypeKey,
    scope: 'SYSTEM_TYPE',
    isSystemForm: true,
    formSource: 'SYSTEM_TYPE',
    baseKey: null,
    userKey: null,
    title,
    titleLower: title.toLowerCase(),
    description: typeof form?.description === 'string'
      ? form.description.trim()
      : (isDefault ? DEFAULT_FORM_DESCRIPTION : ''),
    isDefault,
    layout,
    blocksByKey,
    layoutEdited: Boolean(form?.layoutEdited),
    position: Number.isFinite(Number(form?.position))
      ? Number(form.position)
      : (isDefault ? 0 : now.toMillis()),
    canDelete: !isDefault,
    dateCreated: existingCreated || now,
    dateModified: now,
  };

  delete payload.id;
  delete payload.isVirtual;

  await setDoc(typeFormDocRef(cleanedTypeKey, formKey), payload, { merge: false });
  return normalizeSystemTypeFormRow(cleanedTypeKey, payload, formKey);
}

/**
 * Ensure a system Details form exists for the type (bootstrap layout from schema).
 *
 * @param {string} typeKey
 * @param {object|null} [typeDef]
 * @returns {Promise<object>}
 */
export async function ensureSystemDefaultDetailsForm(typeKey, typeDef = null) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) {
    throw new Error('typeKey is required to ensure a system Details form.');
  }

  const fieldKeys = getVisibleFormFieldKeys(typeDef);
  const scaffold = buildDefaultFormLayoutScaffold({ fieldKeys });

  return saveTypeForm(cleanedTypeKey, {
    blocksByKey: scaffold.blocksByKey,
    description: DEFAULT_FORM_DESCRIPTION,
    isDefault: true,
    key: DEFAULT_FORM_KEY,
    layout: scaffold.layout,
    layoutEdited: false,
    position: 0,
    title: DEFAULT_FORM_TITLE,
  });
}

/**
 * Delete a system-type form template (default Details cannot be deleted).
 *
 * @param {string} typeKey
 * @param {string} formKey
 * @returns {Promise<void>}
 */
export async function deleteTypeForm(typeKey, formKey) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedFormKey = normalizeString(formKey);
  if (!cleanedTypeKey || !cleanedFormKey) return;

  if (cleanedFormKey === DEFAULT_FORM_KEY) {
    throw new Error('The default Details form cannot be deleted.');
  }

  await deleteDoc(typeFormDocRef(cleanedTypeKey, cleanedFormKey));
}

/**
 * Merge system forms ahead of base forms; base rows with the same key are dropped.
 *
 * @param {object[]} systemForms
 * @param {object[]} baseForms
 * @returns {object[]}
 */
export function mergeSystemAndBaseForms(systemForms = [], baseForms = []) {
  const systemRows = (Array.isArray(systemForms) ? systemForms : [])
    .map((row) => normalizeSystemTypeFormRow(row?.typeKey || row?.nodeType, row, row?.key || row?.id));
  const systemKeys = new Set(
    systemRows.map((row) => normalizeString(row?.key || row?.id)).filter(Boolean),
  );

  const baseRows = (Array.isArray(baseForms) ? baseForms : []).filter((row) => {
    const key = normalizeString(row?.key || row?.id);
    return key && !systemKeys.has(key);
  });

  return sortLayoutsByConfiguredOrder([...systemRows, ...baseRows]);
}
